import React, { useState, useEffect } from "react";
import { ShieldCheck, RotateCcw, Trash2, Check, AlertTriangle, HardDrive, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { bridgeActions, isConsentGiven, hasSavedData, clearSaved } from "@/lib/dreamBridge";
export default function SavedProgressControls() {
  const [consent, setConsentState] = useState<boolean>(() => isConsentGiven());
  const [hasData, setHasDataState] = useState<boolean>(() => hasSavedData());
  const [showResetConfirm, setShowResetConfirm] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  // Sync state on mount
  useEffect(() => {
    setConsentState(isConsentGiven());
    setHasDataState(hasSavedData());
  }, []);
  const handleToggleConsent = (checked: boolean) => {
    bridgeActions.setConsent(checked);
    setConsentState(checked);
    setHasDataState(hasSavedData());
    if (checked) {
      setFeedback("Progress will now be saved locally on this browser.");
    } else {
      setFeedback("Local saving switched off and stored progress cleared.");
    }
    setTimeout(() => setFeedback(null), 3600);
  };
  const handleClearSaved = () => {
    clearSaved();
    bridgeActions.setConsent(false);
    setConsentState(false);
    setHasDataState(false);
    setFeedback("Saved progress cleared from this device.");
    setTimeout(() => setFeedback(null), 3600);
  };
  const handleResetAll = () => {
    bridgeActions.resetAll();
    setConsentState(false);
    setHasDataState(false);
    setShowResetConfirm(false);
    setFeedback("The Bridge has been reset to its starting state.");
    setTimeout(() => setFeedback(null), 3600);
  };
  return <div aria-label="Progress and storage controls" className="ql-glass-strong rounded-2xl p-4 sm:p-6 border-primary/25 space-y-4 text-xs max-w-4xl mx-auto w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Toggle switch area */}
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="pt-0.5 shrink-0">
            <Switch id="bridge-save-consent" checked={consent} onCheckedChange={handleToggleConsent} aria-describedby="bridge-save-desc" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor="bridge-save-consent" className="text-xs sm:text-sm font-semibold text-foreground cursor-pointer flex items-center gap-1.5 font-mono">
              <HardDrive className="size-3.5 text-primary shrink-0" />
              <span>Save my progress on this device</span>
            </Label>
            <p id="bridge-save-desc" className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed break-words">
              Off by default. When enabled, your chosen persona, aspirations, plan progress, and weekly notes remain in this browser's local storage. Your optional reflection answers are <strong>never</strong> saved or stored. Nothing is ever sent to a server.
            </p>
          </div>
        </div>

        {/* Action Buttons: Clear & Reset */}
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto shrink-0 pt-2 sm:pt-0">
          {(hasData || consent) && <button type="button" onClick={handleClearSaved} className="min-h-[44px] px-3.5 rounded-full border border-border bg-card/60 hover:bg-card text-muted-foreground hover:text-foreground text-xs font-mono inline-flex items-center gap-1.5 transition-colors cursor-pointer">
              <Trash2 className="size-3.5 shrink-0" />
              <span>Clear saved progress</span>
            </button>}

          {!showResetConfirm ? <button type="button" onClick={() => setShowResetConfirm(true)} className="min-h-[44px] px-3.5 rounded-full border border-border/80 bg-background/50 hover:bg-card text-muted-foreground hover:text-foreground text-xs font-mono inline-flex items-center gap-1.5 transition-colors cursor-pointer">
              <RotateCcw className="size-3.5 shrink-0" />
              <span>Start the Bridge over</span>
            </button> : <div className="flex flex-col min-[400px]:flex-row items-stretch min-[400px]:items-center gap-2 p-1.5 min-[400px]:p-1 bg-destructive/10 border border-destructive/40 rounded-xl min-[400px]:rounded-full w-full min-[400px]:w-auto">
              <span className="text-[11px] font-mono text-destructive px-2 text-center min-[400px]:text-left">
                Reset everything?
              </span>
              <div className="flex items-center gap-1.5 justify-center">
                <button type="button" onClick={handleResetAll} className="min-h-[44px] px-3 rounded-full bg-destructive text-destructive-foreground hover:opacity-90 font-mono text-xs font-semibold cursor-pointer flex-1 min-[400px]:flex-initial flex items-center justify-center">
                  Yes, reset
                </button>
                <button type="button" onClick={() => setShowResetConfirm(false)} className="min-h-[44px] px-2.5 rounded-full text-muted-foreground hover:text-foreground font-mono text-xs cursor-pointer flex-1 min-[400px]:flex-initial flex items-center justify-center">
                  Cancel
                </button>
              </div>
            </div>}
        </div>
      </div>

      {/* Confirmation / feedback banner */}
      {feedback && <div role="status" aria-live="polite" className="pt-1 flex items-center gap-2 text-xs font-mono text-primary">
          <Check className="size-3.5 shrink-0" />
          <span className="break-words">{feedback}</span>
        </div>}
    </div>;
}